<template>
  <div>
    <PageHeaderLayout>
      <div class="main-page-content">
        <div class="page-title-section">
          <div class="title-content">
            <h1 class="page-title"><i class="el-icon-service"></i> 智能客服工作台</h1>
            <p class="page-subtitle">AI 会话监控 · 人工接管 · 线索收集 · 客服配置</p>
          </div>
        </div>

        <div class="action-card">
          <div class="action-buttons">
            <el-badge :value="pendingHuman > 99 ? '99+' : pendingHuman" :hidden="!pendingHuman" class="pending-badge">
              <el-button size="mini" type="warning" plain>待接入人工 {{ pendingHuman }}</el-button>
            </el-badge>
            <el-button type="primary" size="mini" icon="el-icon-setting" @click="openConfig">客服配置</el-button>
            <el-button size="mini" :type="viewTab === 'kb' ? 'success' : 'default'" plain icon="el-icon-reading"
              @click="toggleViewTab">{{ viewTab === 'kb' ? '返回接待' : '知识与意图' }}</el-button>
            <el-button size="mini" icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
            <el-tooltip content="新消息/转人工提醒：声音 + 桌面通知" placement="bottom">
              <el-button size="mini" :type="soundOn ? 'success' : 'info'" plain @click="toggleSound">
                {{ soundOn ? '🔔 提醒开' : '🔕 提醒关' }}
              </el-button>
            </el-tooltip>
          </div>
          <div class="stats-strip" v-if="stats">
            <span>近 {{ stats.days }} 天</span>
            <span>会话 {{ stats.total_sessions }}</span>
            <span>人工 {{ stats.human_sessions }}（{{ stats.handoff_rate }}%）</span>
            <span>CSAT {{ stats.csat_avg }}（{{ stats.csat_count }}）</span>
            <span>首响中位 {{ stats.median_first_reply_secs }}s</span>
            <span v-if="topIntents.length">热点 {{ topIntents.join(' / ') }}</span>
          </div>
        </div>

        <!-- 知识与意图管理面板（AI 检索来源总览 + 意图规则 + 检索测试） -->
        <div v-if="viewTab === 'kb'" class="kb-panel">
          <el-row :gutter="14">
            <el-col :span="8" v-for="src in kbSources" :key="src.source">
              <div class="kb-card">
                <div class="kb-card-head">
                  <span class="kb-card-title">{{ src.source }}</span>
                  <el-button size="mini" type="text" icon="el-icon-edit" @click="$router.push(src.manage_url)">去维护</el-button>
                </div>
                <div class="kb-card-body">
                  <div class="kb-stat"><span class="kb-num">{{ src.active }}</span><span class="kb-label">进检索（启用）</span></div>
                  <div class="kb-stat"><span class="kb-num kb-num--muted">{{ src.total }}</span><span class="kb-label">全部</span></div>
                  <div class="kb-upd">最近更新 {{ src.last_updated || '—' }}</div>
                </div>
              </div>
            </el-col>
          </el-row>

          <el-card shadow="never" class="kb-section">
            <div slot="header" class="kb-section-head">
              <span>文档知识库{{ docStatsText }}</span>
              <el-button size="mini" type="text" icon="el-icon-refresh" @click="loadKbDocs">刷新</el-button>
            </div>
            <el-upload
              class="kb-doc-uploader"
              drag
              action="/service/chat/kb_doc/upload"
              :headers="uploadHeaders"
              name="file"
              accept=".txt,.md,.csv,.pdf,.docx,.xlsx,.xls"
              :show-file-list="false"
              :before-upload="beforeDocUpload"
              :on-success="onDocUploadSuccess"
              :on-error="onDocUploadError">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将文档拖到此处，或<em>点击上传</em></div>
              <div class="el-upload__tip" slot="tip">支持 PDF / Word / Excel / TXT / MD / CSV，单个不超过 32MB，上传后自动解析分块</div>
            </el-upload>
            <el-table v-if="kbDocs.length" :data="kbDocs" size="mini" border style="margin-top:10px">
              <el-table-column label="文件名" min-width="200" show-overflow-tooltip>
                <template slot-scope="{row}">
                  <a :href="row.file_url" target="_blank" rel="noopener" class="kb-doc-name">{{ row.file_name || row.title }}</a>
                </template>
              </el-table-column>
              <el-table-column label="大小" width="80">
                <template slot-scope="{row}">{{ formatSize(row.file_size_bytes) }}</template>
              </el-table-column>
              <el-table-column label="块数" width="60" align="center">
                <template slot-scope="{row}">{{ row.chunk_count || 0 }}</template>
              </el-table-column>
              <el-table-column label="状态" width="90">
                <template slot-scope="{row}">
                  <el-tooltip v-if="row.parse_error" :content="row.parse_error" placement="top">
                    <el-tag size="mini" type="danger">解析失败</el-tag>
                  </el-tooltip>
                  <el-tag v-else-if="row.status === 'active'" size="mini" type="success">启用</el-tag>
                  <el-tag v-else size="mini" type="info">停用</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="上传时间" width="100">
                <template slot-scope="{row}">{{ formatTime(row.created_at) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="130" align="center">
                <template slot-scope="{row}">
                  <el-switch size="mini" :value="row.status === 'active' && !row.parse_error"
                    @change="v => toggleDocStatus(row, v)"></el-switch>
                  <el-button size="mini" type="text" class="kb-doc-del" icon="el-icon-delete" @click="deleteDoc(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div v-else class="kb-hint" style="margin-top:10px">上传 PDF/Word/Excel/TXT 文档，AI 客服将自动检索文档内容回答客户问题</div>
          </el-card>

          <el-card shadow="never" class="kb-section">
            <div slot="header" class="kb-section-head">
              <span>意图识别规则（当前生效）</span>
              <el-button size="mini" type="text" icon="el-icon-edit" @click="intentEditing = !intentEditing">
                {{ intentEditing ? '收起编辑' : '自定义规则' }}
              </el-button>
            </div>
            <el-table v-if="!intentEditing" :data="kbIntentRules" size="mini" border>
              <el-table-column prop="name" label="意图" width="80">
                <template slot-scope="{row}"><el-tag size="mini" effect="plain">{{ row.name }}</el-tag></template>
              </el-table-column>
              <el-table-column label="命中关键词（任一包含即命中，按行序优先）">
                <template slot-scope="{row}">{{ (row.patterns || []).join('、') }}</template>
              </el-table-column>
            </el-table>
            <div v-else>
              <p class="kb-hint">每行一条规则：<code>标签|关键词1,关键词2|回复策略提示(可选)</code>。标签白名单：price / material / order / after_sales / handoff / lead / greeting。配置后完全替代内置规则；第三段会覆盖该意图的 AI 回复策略提示。留空恢复内置。</p>
              <el-input type="textarea" :rows="7" v-model="intentConfigText" placeholder="例：&#10;price|price,cost,报价,多少钱|Never quote numbers, guide to online quote and collect email"></el-input>
              <div style="margin-top:8px;text-align:right">
                <el-button size="mini" @click="intentEditing = false">取消</el-button>
                <el-button size="mini" type="primary" :loading="intentSaving" @click="saveIntentRules">保存规则</el-button>
              </div>
            </div>
          </el-card>

          <el-card shadow="never" class="kb-section">
            <div slot="header" class="kb-section-head"><span>检索测试（与线上 AI 同一条检索链路）</span></div>
            <div class="kb-search-row">
              <el-input v-model="kbQuery" size="small" placeholder="输入访客可能问的话，例如：SLA 打印精度多少 / how much for 100 CNC parts" clearable
                @keyup.enter.native="runKbSearch"></el-input>
              <el-button size="small" type="primary" :loading="kbSearching" :disabled="!kbQuery.trim()" @click="runKbSearch">测试</el-button>
            </div>
            <el-table v-if="kbResults" :data="kbResults" size="mini" border style="margin-top:10px">
              <el-table-column prop="source" label="来源" width="80"></el-table-column>
              <el-table-column prop="title" label="标题" width="240" show-overflow-tooltip></el-table-column>
              <el-table-column prop="score" label="相关度" width="70"></el-table-column>
              <el-table-column prop="content" label="内容摘要" show-overflow-tooltip></el-table-column>
            </el-table>
            <div v-else class="kb-hint" style="margin-top:8px">未命中时 AI 会走通用回复并引导留联系方式——可去对应内容管理页补充知识。</div>
          </el-card>
        </div>

        <div v-else class="chat-workspace">
          <!-- 左：会话列表 -->
          <div class="session-list" v-loading="loading">
            <div class="session-filter">
              <el-radio-group v-model="filterMode" size="mini" @change="page = 1; loadSessions()">
                <el-radio-button label="">全部</el-radio-button>
                <el-radio-button label="1">人工模式</el-radio-button>
                <el-radio-button label="0">AI 模式</el-radio-button>
                <el-radio-button label="mine">我的会话</el-radio-button>
                <el-radio-button label="unassigned">未分配</el-radio-button>
              </el-radio-group>
            </div>
            <div v-if="sessions.length === 0 && !loading" class="session-empty">暂无会话</div>
            <div v-for="s in sessions" :key="s.id" class="session-item" :class="{ active: current && current.id === s.id }" @click="openSession(s)">
              <div class="session-item-head">
                <span class="session-name">{{ sessionName(s) }}</span>
                <el-tag size="mini" :type="s.mode === '1' ? 'danger' : 'success'">{{ s.mode === '1' ? '人工' : 'AI' }}</el-tag>
              </div>
              <div class="session-last">{{ s.last_message || '（无消息）' }}</div>
              <div class="session-item-foot">
                <span class="session-time">{{ formatTime(s.last_message_at) }}</span>
                <span class="session-intents">
                  <el-tag v-if="intentLabel(s.intent)" size="mini" effect="plain" type="info">{{ intentLabel(s.intent) }}</el-tag>
                  <span v-if="s.admin_name" class="session-owner">👤 {{ s.admin_name }}</span>
                </span>
                <el-badge v-if="s.unread_admin > 0" :value="s.unread_admin" class="session-unread" />
              </div>
            </div>
            <el-pagination v-if="total > pageSize" style="margin-top:8px;text-align:center" layout="prev, pager, next" small
              :total="total" :current-page.sync="page" :page-size="pageSize" @current-change="loadSessions"></el-pagination>
          </div>

          <!-- 右：聊天窗 -->
          <div class="chat-pane">
            <template v-if="current">
              <div class="chat-pane-head">
                <div>
                  <div class="chat-title">{{ sessionName(current) }}
                    <el-tag v-if="current.admin_name" size="mini" type="primary" style="margin-left:6px">{{ current.admin_name }} 处理中</el-tag>
                    <el-tag v-else-if="current.mode === '1'" size="mini" type="warning" style="margin-left:6px">未分配</el-tag>
                  </div>
                  <div class="chat-sub">
                    <span v-if="current.guest_email">📧 {{ current.guest_email }}</span>
                    <span v-if="current.guest_phone">📱 {{ current.guest_phone }}</span>
                    <span v-if="current.customer_id" class="chat-bound">已绑定客户</span>
                  </div>
                </div>
                <div>
                  <el-button v-if="current.mode === '1' && !isMine(current)" size="mini" type="primary" plain @click="claimSession">认领接待</el-button>
                  <el-button v-if="current.mode === '1' && isMine(current)" size="mini" type="info" plain @click="releaseSession">释放</el-button>
                  <el-button v-if="current.mode === '0'" size="mini" type="warning" plain @click="switchMode('1')">接管（停AI）</el-button>
                  <el-button v-else size="mini" type="success" plain @click="switchMode('0')">切回AI</el-button>
                </div>
              </div>
              <div class="chat-msgs" ref="msgsBox" v-loading="msgLoading">
                <div v-for="m in messages" :key="m.id" class="chat-msg" :class="msgClass(m)">
                  <div class="chat-msg-meta">{{ msgRole(m) }}<span v-if="m.created_at"> · {{ formatTime(m.created_at) }}</span></div>
                  <!-- 图片附件 -->
                  <div v-if="m.msg_type === 'image' && parseExtra(m.extra)" class="chat-msg-attachment">
                    <el-image :src="parseExtra(m.extra).url" :preview-src-list="[parseExtra(m.extra).url]" fit="cover" class="chat-attach-img" />
                  </div>
                  <!-- 文件 / 3D 模型附件 -->
                  <div v-else-if="(m.msg_type === 'file' || m.msg_type === 'model') && parseExtra(m.extra)" class="chat-msg-attachment">
                    <a :href="parseExtra(m.extra).url" target="_blank" rel="noopener" class="chat-attach-file">
                      <span class="chat-attach-icon">{{ m.msg_type === 'model' ? '🧊' : '📎' }}</span>
                      <span>
                        <div class="chat-attach-name">{{ parseExtra(m.extra).name || (m.msg_type === 'model' ? '3D model' : 'file') }}</div>
                        <div class="chat-attach-size">{{ formatSize(parseExtra(m.extra).size) }}</div>
                      </span>
                    </a>
                  </div>
                  <div v-else class="chat-msg-body">{{ m.content }}</div>
                </div>
                <div v-if="messages.length === 0 && !msgLoading" class="session-empty">无消息</div>
              </div>
              <div class="chat-inputbar">
                <el-input v-model="replyText" size="small" placeholder="输入回复内容，Enter 发送" @keydown.enter.native="sendReply"
                  :disabled="sending" type="textarea" :autosize="{ minRows: 1, maxRows: 4 }"></el-input>
                <el-button type="primary" size="small" :loading="sending" :disabled="!replyText.trim()" @click="sendReply">发送</el-button>
              </div>
            </template>
            <div v-else class="chat-pane chat-pane-empty">
              <i class="el-icon-chat-dot-round"></i>
              <p>选择左侧会话开始接待</p>
            </div>
          </div>
        </div>
      </div>
    </PageHeaderLayout>

    <!-- 客服配置 -->
    <el-dialog title="AI 客服配置" :visible.sync="configVisible" width="640px" append-to-body>
      <el-form label-width="130px" size="small" v-loading="configLoading">
        <el-form-item label="挂件开关">
          <el-switch v-model="config.enabled" active-text="开启" inactive-text="关闭"></el-switch>
        </el-form-item>
        <el-form-item label="LLM Base URL">
          <el-input v-model="config.base_url" placeholder="OpenAI 兼容地址；留空复用翻译配置"></el-input>
        </el-form-item>
        <el-form-item label="LLM API Key">
          <el-input v-model="config.api_key" :placeholder="config.api_key_set ? '已设置（留空不修改）' : '未设置（留空复用翻译配置）'"></el-input>
        </el-form-item>
        <el-form-item label="模型">
          <el-input v-model="config.model" placeholder="如 glm-4.6-flash；留空复用翻译配置"></el-input>
        </el-form-item>
        <el-form-item label="系统提示词">
          <el-input v-model="config.system_prompt" type="textarea" :rows="4" placeholder="AI 客服人设与规则；留空用内置默认"></el-input>
        </el-form-item>
        <el-form-item label="欢迎语">
          <el-input v-model="config.welcome" placeholder="访客打开聊天窗看到的第一句"></el-input>
        </el-form-item>
        <el-form-item label="社媒链接">
          <el-input v-model="config.social_links_text" type="textarea" :rows="3" placeholder='JSON 数组，如 [{"type":"whatsapp","url":"https://wa.me/86xxx","name":"WhatsApp"}]'></el-input>
        </el-form-item>
        <el-form-item label="表单链接">
          <el-input v-model="config.form_url" placeholder="客户自主递交表单的链接（窗内按钮跳转）"></el-input>
        </el-form-item>
        <el-form-item label="线索收集引导">
          <el-input v-model="config.lead_fields" placeholder="喂给 AI 的收集目标，如：称呼、邮箱、加工需求、数量、材料"></el-input>
        </el-form-item>
        <el-form-item label="每访客日限">
          <el-input-number v-model="config.daily_limit" :min="10" :max="2000"></el-input-number>
        </el-form-item>
        <el-form-item label="转人工关键词">
          <el-input v-model="config.handoff_keywords" placeholder="逗号分隔，如 human,人工,complain,投诉"></el-input>
        </el-form-item>
        <el-form-item label="主动问候延迟">
          <el-input-number v-model="config.greeting_delay" :min="0" :max="300"></el-input-number>
          <span style="margin-left:8px;color:#909399;font-size:12px">秒；0=关闭（访客停留 N 秒后自动弹欢迎气泡）</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="configVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="configSaving" @click="saveConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeaderLayout from '@/layouts/PageHeaderLayout'
import { buildUploadAuthHeaders } from '@/utils/request'

export default {
  name: 'chat-workbench',
  components: { PageHeaderLayout },
  data() {
    return {
      loading: false,
      sessions: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filterMode: '',
      current: null,
      messages: [],
      msgLoading: false,
      replyText: '',
      sending: false,
      pendingHuman: 0,
      pollTimer: null,
      // 当前客服（localStorage user_info.id；sessions 过滤"我的会话"用）
      myAdminId: '',
      // 声音/桌面通知提醒开关（默认开；浏览器策略要求用户先交互后才能出声）
      soundOn: true,
      audioCtx: null,
      // 轮询之间检测变化触发提醒（而非每次轮询都响）
      lastPendingHuman: 0,
      lastTotalUnread: 0,
      // 近 30 天运营统计（POST /service/chat/stats）
      stats: null,
      // 知识与意图面板（viewTab: 'sessions' 接待视图 / 'kb' 知识意图视图）
      viewTab: 'sessions',
      kbSources: [],
      kbIntentRules: [],
      intentConfigText: '',
      intentEditing: false,
      intentSaving: false,
      kbQuery: '',
      kbSearching: false,
      kbResults: null,
      // 文档知识库（POST /service/chat/kb_doc/*）
      uploadHeaders: {},
      kbDocs: [],
      kbDocTotal: 0,
      kbDocPage: 1,
      kbDocPageSize: 20,
      kbOverview: null,
      // 配置
      configVisible: false,
      configLoading: false,
      configSaving: false,
      config: {
        enabled: true, base_url: '', api_key: '', api_key_set: false, model: '',
        system_prompt: '', welcome: '', social_links_text: '', form_url: '',
        lead_fields: '', daily_limit: 200, handoff_keywords: '',
        // 十审 W-3：主动问候延迟（秒，0=关）——后端链路早已就绪，此处补配置入口
        greeting_delay: 0
      }
    }
  },
  created() {
    try {
      const ui = JSON.parse(localStorage.getItem('user_info') || '{}')
      this.myAdminId = String(ui.id || '')
    } catch (e) { this.myAdminId = '' }
    this.loadSessions()
    this.loadUnread(true)
    this.loadStats()
    // 评审 U4：会话列表也纳入轮询——原实现只刷未读数与当前会话消息，
    // 新访客发消息后列表不出现该会话，客服需手动刷新才能看到
    this.pollTimer = setInterval(() => { this.loadUnread(); this.loadSessions(); if (this.current) this.loadMessages(false) }, 8000)
    // 桌面通知：不在挂载时强弹权限；首次响铃路径再请求（见 maybeAskNotifyPermission）
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  computed: {
    totalUnread() {
      return this.sessions.reduce((sum, s) => sum + (Number(s.unread_admin) || 0), 0)
    },
    docStatsText() {
      const d = this.kbOverview || {}
      const docs = Number(d.doc_count)
      const chunks = Number(d.doc_chunk_count)
      if (!Number.isFinite(docs) || !Number.isFinite(chunks)) return ''
      return `（文档 ${docs} 篇 / ${chunks} 块）`
    },
    topIntents() {
      const dist = (this.stats && this.stats.intent_dist) || {}
      const labels = { price: '价格', material: '材料', order: '订单', after_sales: '售后', handoff: '转人工', lead: '线索', greeting: '问候' }
      return Object.keys(dist)
        .sort((a, b) => dist[b] - dist[a])
        .slice(0, 3)
        .map(k => `${labels[k] || k} ${dist[k]}`)
    }
  },
  methods: {
    intentLabel(it) {
      return { price: '价格', material: '材料', order: '订单', after_sales: '售后', handoff: '转人工', lead: '线索', greeting: '问候' }[it] || ''
    },
    sessionName(s) {
      return s.guest_name || s.guest_email || ('访客 ' + String(s.visitor_key || '').slice(0, 8))
    },
    isMine(s) {
      return !!this.myAdminId && s.admin_id === this.myAdminId
    },
    msgClass(m) {
      return { visitor: 'from-visitor', ai: 'from-ai', agent: 'from-agent', system: 'from-system' }[m.sender_type] || 'from-ai'
    },
    msgRole(m) {
      return { visitor: '访客', ai: 'AI', agent: (m.sender_name || '客服'), system: '系统' }[m.sender_type] || m.sender_type
    },
    parseExtra(raw) {
      if (!raw) return null
      try { return JSON.parse(raw) } catch (e) { return null }
    },
    formatSize(bytes) {
      if (!bytes || bytes <= 0) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      if (isNaN(d.getTime())) return String(t)
      const p = (n) => String(n).padStart(2, '0')
      return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    // ===== 提醒：声音（Web Audio 合成铃声，免音频文件）+ 桌面通知 =====
    toggleSound() {
      this.soundOn = !this.soundOn
      if (this.soundOn) {
        this.playChime()
        // 终审：仅在用户主动开提醒时申请桌面通知权限
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          Notification.requestPermission().catch(() => {})
        }
      }
    },
    playChime() {
      if (!this.soundOn) return
      try {
        if (!this.audioCtx) {
          const AC = window.AudioContext || window.webkitAudioContext
          if (!AC) return
          this.audioCtx = new AC()
        }
        if (this.audioCtx.state === 'suspended') this.audioCtx.resume()
        const ctx = this.audioCtx
        const now = ctx.currentTime
        // 双音提示音（叮-咚）：880Hz + 660Hz 各 0.18s
        ;[[880, 0], [660, 0.18]].forEach(([freq, delay]) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.type = 'sine'
          osc.frequency.value = freq
          gain.gain.setValueAtTime(0.001, now + delay)
          gain.gain.exponentialRampToValueAtTime(0.25, now + delay + 0.02)
          gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.18)
          osc.connect(gain).connect(ctx.destination)
          osc.start(now + delay)
          osc.stop(now + delay + 0.2)
        })
      } catch (e) { /* 无声环境下静默 */ }
    },
    desktopNotify(title, body) {
      try {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
        const n = new Notification(title, { body, tag: 'ola-chat', icon: undefined })
        n.onclick = () => { window.focus(); n.close() }
      } catch (e) { /* 静默 */ }
    },
    refreshAll() {
      this.loadSessions()
      this.loadUnread(true)
      this.loadStats()
      // 刷新按钮语义是"数据刷新"不是"页面刷新"：当前打开的会话消息也要重拉
      if (this.current) this.loadMessages(false)
    },
    // ===== 知识与意图面板 =====
    toggleViewTab() {
      this.viewTab = this.viewTab === 'kb' ? 'sessions' : 'kb'
      if (this.viewTab === 'kb') {
        this.uploadHeaders = buildUploadAuthHeaders()
        this.loadKbOverview()
        this.loadKbDocs()
      }
    },
    async loadKbOverview() {
      try {
        const res = await this.$api.post('/service/chat/kb_overview', {})
        if (this.$apiSuccess(res)) {
          const d = res.data || {}
          this.kbSources = d.sources || []
          this.kbIntentRules = d.intent_rules || []
          this.kbOverview = d
          if (!this.intentEditing) this.intentConfigText = d.intent_config || ''
        }
      } catch (e) { /* 静默 */ }
    },
    // ===== 文档知识库 =====
    beforeDocUpload(file) {
      this.uploadHeaders = buildUploadAuthHeaders()
      if (file.size > 32 * 1024 * 1024) {
        this.$message.error('文件超过 32MB 上限')
        return false
      }
      return true
    },
    onDocUploadSuccess(res) {
      if (this.$apiSuccess(res)) {
        this.$message.success('上传成功，已进入解析')
      } else {
        this.$message.error((res && res.message) || '上传失败')
      }
      this.loadKbDocs()
      this.loadKbOverview()
    },
    onDocUploadError() {
      this.$message.error('上传失败，请重试')
    },
    async loadKbDocs() {
      try {
        const res = await this.$api.post('/service/chat/kb_doc/list', {
          page: this.kbDocPage,
          page_size: this.kbDocPageSize
        })
        if (this.$apiSuccess(res)) {
          const d = res.data || {}
          this.kbDocs = d.list || []
          this.kbDocTotal = Number((d.page && d.page.total) || d.total) || 0
        }
      } catch (e) { /* 静默 */ }
    },
    async toggleDocStatus(row, on) {
      try {
        const res = await this.$api.post('/service/chat/kb_doc/status', {
          id: row.id,
          status: on ? 'active' : 'disabled'
        })
        if (this.$apiSuccess(res)) {
          this.$message.success(on ? '已启用，进入 AI 检索' : '已停用')
          this.loadKbDocs()
          this.loadKbOverview()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    deleteDoc(row) {
      this.$confirm(`确定删除文档「${row.file_name || row.title}」？删除后 AI 不再检索其内容`, '删除文档', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api.post('/service/chat/kb_doc/delete', { id: row.id })
          if (this.$apiSuccess(res)) {
            this.$message.success('已删除')
            this.loadKbDocs()
            this.loadKbOverview()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (e) {
          this.$message.error((e && e.message) || '删除失败')
        }
      }).catch(() => {})
    },
    async saveIntentRules() {
      this.intentSaving = true
      try {
        const res = await this.$api.post('/service/chat/config_save', { intent_rules: this.intentConfigText })
        if (this.$apiSuccess(res)) {
          this.$message.success('意图规则已保存（立即生效）')
          this.intentEditing = false
          await this.loadKbOverview()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '保存失败')
      }
      this.intentSaving = false
    },
    async runKbSearch() {
      if (!this.kbQuery.trim()) return
      this.kbSearching = true
      try {
        const res = await this.$api.post('/service/chat/kb_search', { query: this.kbQuery.trim() })
        if (this.$apiSuccess(res)) {
          this.kbResults = (res.data && res.data.list) || []
        }
      } catch (e) { /* 静默 */ }
      this.kbSearching = false
    },
    async loadStats() {
      try {
        const res = await this.$api.post('/service/chat/stats', { days: 30 })
        if (this.$apiSuccess(res)) {
          this.stats = res.data || null
        }
      } catch (e) { /* 静默 */ }
    },
    async loadSessions() {
      this.loading = true
      try {
        // mine/unassigned 为前端过滤语义（后端按 admin_id 过滤）
        const payload = { page: this.page, page_size: this.pageSize }
        if (this.filterMode === 'mine') {
          if (!this.myAdminId) {
            this.$message && this.$message.warning('无法识别当前客服身份，请重新登录后再看「我的」')
            this.sessions = []
            this.total = 0
            return
          }
          payload.admin_id = this.myAdminId
        } else if (this.filterMode === 'unassigned') payload.unassigned = 1
        else if (this.filterMode !== '') payload.mode = this.filterMode
        const res = await this.$api.post('/service/chat/sessions', payload)
        if (this.$apiSuccess(res)) {
          const d = res.data || {}
          const prevUnread = {}
          this.sessions.forEach(s => { prevUnread[s.id] = Number(s.unread_admin) || 0 })
          this.sessions = d.list || []
          this.total = Number((d.page && d.page.total) || 0)
          // 变化检测：某会话未读数增加 → 响铃 + 桌面通知
          let alertSession = null
          this.sessions.forEach(s => {
            const n = Number(s.unread_admin) || 0
            if (n > (prevUnread[s.id] || 0)) alertSession = s
          })
          if (alertSession) {
            this.playChime()
            this.desktopNotify('💬 ' + this.sessionName(alertSession), (alertSession.last_message || '新消息').slice(0, 80))
          }
        }
      } catch (e) { /* 静默 */ }
      this.loading = false
    },
    async loadUnread(silent) {
      try {
        const res = await this.$api.post('/service/chat/unread_count')
        if (this.$apiSuccess(res)) {
          const prev = this.pendingHuman
          this.pendingHuman = Number((res.data && res.data.pending_human) || 0)
          // 新转人工会话到达 → 响铃（不重复响：只在数字增加时）
          if (!silent && this.pendingHuman > prev) {
            this.playChime()
            this.desktopNotify('⚠️ 客户请求转人工', `${this.pendingHuman} 个会话等待人工接入`)
          }
        }
      } catch (e) { /* 静默 */ }
    },
    async openSession(s) {
      this.current = s
      this.loadMessages(true)
    },
    // 客服缺口4：显式认领（人工模式未分配时抢占；幂等——自己已是负责人无感重认领）
    async claimSession() {
      if (!this.current) return
      try {
        const res = await this.$api.post('/service/chat/claim', { session_id: this.current.id, action: 'claim' })
        if (this.$apiSuccess(res)) {
          this.$message.success('已认领，AI 停止自动回复，由你接待')
          await this.loadMessages(false)
          this.loadSessions()
        } else {
          this.$message.error(res.message || '认领失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '认领失败')
      }
    },
    // 释放会话（下班/转交同事）：清空负责人，回到未分配池
    async releaseSession() {
      if (!this.current) return
      try {
        const res = await this.$api.post('/service/chat/claim', { session_id: this.current.id, action: 'release' })
        if (this.$apiSuccess(res)) {
          this.$message.success('已释放，会话回到未分配池')
          await this.loadMessages(false)
          this.loadSessions()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    async loadMessages(scroll) {
      if (!this.current) return
      this.msgLoading = true
      try {
        const res = await this.$api.post('/service/chat/detail', { session_id: this.current.id })
        if (this.$apiSuccess(res)) {
          const d = res.data || {}
          this.messages = d.list || []
          if (d.session) {
            this.current = Object.assign({}, this.current, d.session, { unread_admin: 0 })
          }
          if (scroll !== false) this.scrollToBottom()
        }
      } catch (e) { /* 静默 */ }
      this.msgLoading = false
      if (scroll !== false) this.$nextTick(() => this.scrollToBottom())
    },
    scrollToBottom() {
      const box = this.$refs.msgsBox
      if (box) box.scrollTop = box.scrollHeight
    },
    async sendReply() {
      if (!this.current || !this.replyText.trim() || this.sending) return
      this.sending = true
      const content = this.replyText.trim()
      try {
        const res = await this.$api.post('/service/chat/reply', { session_id: this.current.id, content })
        if (this.$apiSuccess(res)) {
          this.replyText = ''
          await this.loadMessages(false)
          this.scrollToBottom()
        } else {
          this.$message.error(res.message || '发送失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '发送失败')
      }
      this.sending = false
    },
    async switchMode(mode) {
      if (!this.current) return
      try {
        const res = await this.$api.post('/service/chat/mode', { session_id: this.current.id, mode })
        if (this.$apiSuccess(res)) {
          this.$message.success(mode === '1' ? '已接管，AI 停止自动回复' : '已切回 AI 自动回复')
          await this.loadMessages(false)
          this.loadSessions()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '操作失败')
      }
    },
    async openConfig() {
      this.configVisible = true
      this.configLoading = true
      try {
        const res = await this.$api.post('/service/chat/config_get')
        if (this.$apiSuccess(res)) {
          const d = res.data || {}
          this.config.enabled = d.enabled !== false
          this.config.base_url = d.base_url || ''
          this.config.model = d.model || ''
          this.config.system_prompt = d.system_prompt || ''
          this.config.welcome = d.welcome || ''
          this.config.form_url = d.form_url || ''
          this.config.lead_fields = d.lead_fields || ''
          this.config.daily_limit = Number(d.daily_limit) || 200
          this.config.handoff_keywords = d.handoff_keywords || ''
          this.config.greeting_delay = Number(d.greeting_delay) || 0
          this.config.api_key_set = !!d.api_key_set
          this.config.api_key = ''
          let social = d.social_links || '[]'
          if (typeof social !== 'string') { try { social = JSON.stringify(social) } catch (e) { social = '[]' } }
          this.config.social_links_text = social === 'null' ? '' : social
        }
      } catch (e) { /* 静默 */ }
      this.configLoading = false
    },
    async saveConfig() {
      let social = []
      try {
        social = this.config.social_links_text ? JSON.parse(this.config.social_links_text) : []
      } catch (e) {
        this.$message.error('社媒链接 JSON 格式不对')
        return
      }
      const payload = {
        enabled: this.config.enabled,
        base_url: this.config.base_url,
        model: this.config.model,
        system_prompt: this.config.system_prompt,
        welcome: this.config.welcome,
        social_links: social,
        form_url: this.config.form_url,
        lead_fields: this.config.lead_fields,
        daily_limit: this.config.daily_limit,
        handoff_keywords: this.config.handoff_keywords,
        greeting_delay: this.config.greeting_delay
      }
      if (this.config.api_key) payload.api_key = this.config.api_key
      this.configSaving = true
      try {
        const res = await this.$api.post('/service/chat/config_save', payload)
        if (this.$apiSuccess(res)) {
          this.$message.success('已保存')
          this.configVisible = false
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '保存失败')
      }
      this.configSaving = false
    }
  }
}
</script>

<style lang="stylus" scoped>
.pending-badge
  margin-right 12px
.stats-strip
  margin-top 10px
  display flex
  flex-wrap wrap
  gap 14px
  font-size 12px
  color #666
  line-height 1.6
  span
    white-space nowrap
.chat-workspace
  display flex
  gap 14px
  min-height 620px
.kb-panel
  background transparent
.kb-card
  background #fff
  border-radius 8px
  padding 14px 16px
  margin-bottom 14px
.kb-card-head
  display flex
  justify-content space-between
  align-items center
  margin-bottom 10px
.kb-card-title
  font-weight 600
  font-size 13px
  color #333
.kb-card-body
  display flex
  gap 24px
  align-items baseline
.kb-stat
  display flex
  flex-direction column
.kb-num
  font-size 22px
  font-weight 700
  color #409eff
.kb-num--muted
  color #909399
.kb-label
  font-size 11px
  color #999
.kb-upd
  margin-left auto
  font-size 11px
  color #aaa
.kb-section
  margin-bottom 14px
.kb-section-head
  display flex
  justify-content space-between
  align-items center
  font-weight 600
.kb-hint
  font-size 12px
  color #909399
  line-height 1.8
.kb-search-row
  display flex
  gap 8px
.kb-doc-uploader
  margin-top 4px
  /deep/ .el-upload
    width 100%
  /deep/ .el-upload-dragger
    width 100%
    height 120px
    display flex
    flex-direction column
    justify-content center
    align-items center
  /deep/ .el-upload__tip
    font-size 11px
    color #909399
    margin-top 4px
.kb-doc-name
  color #409eff
  text-decoration none
.kb-doc-del
  margin-left 8px
  color #f56c6c
.session-list
  width 320px
  background #fff
  border-radius 8px
  padding 10px
  overflow-y auto
  max-height 700px
.session-filter
  margin-bottom 8px
.session-empty
  text-align center
  color #999
  padding 40px 0
.session-item
  padding 10px
  border-radius 8px
  cursor pointer
  margin-bottom 6px
  border 1px solid transparent
  &:hover
    background #f7f7f7
  &.active
    background #fdf6ec
    border-color #c47f17
.session-item-head
  display flex
  justify-content space-between
  align-items center
.session-name
  font-weight 600
  font-size 13px
  color #333
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  max-width 190px
.session-last
  font-size 12px
  color #888
  margin 4px 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
.session-item-foot
  display flex
  justify-content space-between
  align-items center
.session-time
  font-size 11px
  color #aaa
.session-intents
  display flex
  align-items center
  gap 4px
  flex 1
  justify-content flex-end
.session-owner
  font-size 11px
  color #409eff
.chat-msg-attachment
  padding 4px 0
.chat-attach-img
  max-width 200px
  max-height 160px
  border-radius 6px
  display block
.chat-attach-file
  display flex
  align-items center
  gap 8px
  color inherit
  text-decoration none
.chat-attach-icon
  font-size 20px
.chat-attach-name
  font-size 12px
  color #409eff
  max-width 160px
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
.chat-attach-size
  font-size 11px
  color #999
.chat-pane
  flex 1
  background #fff
  border-radius 8px
  display flex
  flex-direction column
  overflow hidden
  max-height 700px
.chat-pane-empty
  align-items center
  justify-content center
  color #bbb
  i
    font-size 48px
.chat-pane-head
  padding 12px 16px
  border-bottom 1px solid #f0f0f0
  display flex
  justify-content space-between
  align-items center
.chat-title
  font-weight 600
.chat-sub
  font-size 12px
  color #888
  margin-top 2px
  span
    margin-right 10px
.chat-bound
  color #67c23a
.chat-msgs
  flex 1
  overflow-y auto
  padding 14px
  background #fafafa
.chat-msg
  margin-bottom 12px
  max-width 70%
  padding 8px 12px
  border-radius 8px
  font-size 13px
  line-height 1.55
  white-space pre-wrap
  word-break break-word
.chat-msg-meta
  font-size 11px
  opacity .65
  margin-bottom 2px
.from-visitor
  background #c47f17
  color #fff
  margin-left auto
.from-ai
  background #fff
  border 1px solid #ececec
.from-agent
  background #ecf5ff
  border 1px solid #b3d8ff
.from-system
  background #f0f9eb
  border 1px solid #e1f3d8
  margin 0 auto
  text-align center
  font-size 12px
  color #67c23a
.chat-inputbar
  display flex
  gap 8px
  padding 10px
  border-top 1px solid #f0f0f0
  align-items flex-end
</style>
